/**
 * IDV Grouping Logic
 *
 * Groups award portfolio items by IDV parent for the cash flow explorer.
 * Determines standalone awards, IDV groups, and assistance awards (grants).
 *
 * Grouping rules:
 * - parent_award_piid === null AND category !== 'idv' → standalone
 * - parent_award_piid !== null → child of IDV, group by parent_award_piid
 * - category === 'idv' → IDV parent record
 * - Children without matching parent in portfolio → shared vehicle
 */
import type {
  AwardPortfolioItem,
  IdvGroupTimeSeriesResponse,
} from '@/types/usaspending'

import { type CumulativePoint, safeNum } from './cashflow-utils'

// IDV type code → human label
const IDV_TYPE_LABELS: Record<string, string> = {
  IDV_E: 'BPA',
  IDV_A: 'GWAC',
  IDV_B: 'IDC',
  IDV_B_B: 'IDC',
  IDV_B_C: 'IDC',
  IDV_C: 'FSS',
  IDV_D: 'BOA',
}

export const IDV_BADGE_COLORS: Record<string, string> = {
  BPA: '#a855f7',
  GWAC: '#f59e0b',
  IDC: '#06b6d4',
  FSS: '#06b6d4',
  BOA: '#06b6d4',
}

// Okabe-Ito colorblind-safe palette (distinguishable for deuteranopia/protanopia).
// Avoids problematic red-green combinations; works on both light and dark backgrounds.
export const CHILD_LINE_COLORS = [
  '#56B4E9', // sky blue
  '#E69F00', // orange
  '#009E73', // bluish green
  '#CC79A7', // reddish purple
  '#0072B2', // blue
  '#D55E00', // vermilion
]

/**
 * Returns a distinct color for the i-th IDV child series.
 * Uses the 6-color Okabe-Ito palette first, then generates additional
 * colors via golden-angle hue rotation for large IDVs (59+ TOs).
 */
export function getChildColor(index: number): string {
  if (index < CHILD_LINE_COLORS.length) return CHILD_LINE_COLORS[index]
  const hue = (index * 137.508) % 360
  return `hsl(${hue}, 65%, 50%)`
}

export const ASSISTANCE_CATEGORIES = new Set([
  'grant',
  'direct payment',
  'loans',
  'insurance',
  'other',
])

// -- Selection & view mode types --

export type CashFlowSelection =
  | null
  | { type: 'award'; awardId: string }
  | { type: 'idv'; parentPiid: string }

// -- IDV group types --

export interface IdvGroup {
  parentPiid: string
  parentAward: AwardPortfolioItem | null
  idvType: string | null
  typeLabel: string
  isShared: boolean
  children: AwardPortfolioItem[]
  recipientCeiling: number
  recipientObligation: number
  recipientOutlay: number
}

export interface GroupedPortfolio {
  standaloneContracts: AwardPortfolioItem[]
  idvGroups: IdvGroup[]
  grants: AwardPortfolioItem[]
  totalCeiling: number
  totalObligation: number
  totalOutlay: number
  awardCount: number
}

// -- Functions --

export function getIdvTypeLabel(type: string | null): string {
  if (!type) return 'IDV'
  return IDV_TYPE_LABELS[type] ?? 'IDV'
}

/**
 * Groups award portfolio items into standalone contracts, IDV groups, and grants.
 */
export function groupAwardPortfolio(
  awards: AwardPortfolioItem[],
): GroupedPortfolio {
  const standaloneContracts: AwardPortfolioItem[] = []
  const grants: AwardPortfolioItem[] = []
  const idvParentRecords = new Map<string, AwardPortfolioItem>()
  const childrenByParent = new Map<string, AwardPortfolioItem[]>()

  for (const award of awards) {
    // IDV parent record
    if (award.category === 'idv') {
      if (award.display_award_id) {
        idvParentRecords.set(award.display_award_id, award)
      }
      continue
    }

    // Child of IDV (has parent_award_piid)
    if (award.parent_award_piid) {
      const existing = childrenByParent.get(award.parent_award_piid) ?? []
      existing.push(award)
      childrenByParent.set(award.parent_award_piid, existing)
      continue
    }

    // Assistance awards (grants, etc.)
    if (award.category && ASSISTANCE_CATEGORIES.has(award.category)) {
      grants.push(award)
      continue
    }

    // Standalone contract
    standaloneContracts.push(award)
  }

  // Build IDV groups
  const idvGroups: IdvGroup[] = []
  for (const [parentPiid, children] of childrenByParent) {
    const parentAward = idvParentRecords.get(parentPiid) ?? null
    children.sort(
      (a, b) => safeNum(b.total_obligation) - safeNum(a.total_obligation),
    )

    idvGroups.push({
      parentPiid,
      parentAward,
      idvType: parentAward?.type ?? null,
      typeLabel: getIdvTypeLabel(parentAward?.type ?? null),
      isShared: !parentAward,
      children,
      recipientCeiling: children.reduce(
        (s, c) => s + safeNum(c.base_and_all_options_value),
        0,
      ),
      recipientObligation: children.reduce(
        (s, c) => s + safeNum(c.total_obligation),
        0,
      ),
      recipientOutlay: children.reduce(
        (s, c) => s + safeNum(c.total_outlays),
        0,
      ),
    })
  }

  // Sort by obligation descending
  idvGroups.sort((a, b) => b.recipientObligation - a.recipientObligation)
  standaloneContracts.sort(
    (a, b) => safeNum(b.total_obligation) - safeNum(a.total_obligation),
  )
  grants.sort(
    (a, b) => safeNum(b.total_obligation) - safeNum(a.total_obligation),
  )

  // Compute totals in a single pass, reusing pre-aggregated IDV group values
  let totalCeiling = 0
  let totalObligation = 0
  let totalOutlay = 0
  let awardCount = standaloneContracts.length + grants.length
  for (const a of standaloneContracts) {
    totalCeiling += safeNum(a.base_and_all_options_value)
    totalObligation += safeNum(a.total_obligation)
    totalOutlay += safeNum(a.total_outlays)
  }
  for (const a of grants) {
    totalCeiling += safeNum(a.base_and_all_options_value)
    totalObligation += safeNum(a.total_obligation)
    totalOutlay += safeNum(a.total_outlays)
  }
  for (const g of idvGroups) {
    totalCeiling += g.recipientCeiling
    totalObligation += g.recipientObligation
    totalOutlay += g.recipientOutlay
    awardCount += g.children.length
  }

  return {
    standaloneContracts,
    idvGroups,
    grants,
    totalCeiling,
    totalObligation,
    totalOutlay,
    awardCount,
  }
}

/**
 * Maps each IDV child TO's awardId → its chart color, using the same
 * ordering as buildIdvGroupOption.
 */
export function buildIdvChildColorMap(
  idvGroupTimeSeries: IdvGroupTimeSeriesResponse | null,
  group: IdvGroup | null,
): Map<string, string> {
  const map = new Map<string, string>()
  if (!idvGroupTimeSeries || !group) return map
  idvGroupTimeSeries.children.forEach((child, i) => {
    map.set(child.awardId, getChildColor(i))
  })
  return map
}

/**
 * Estimate a linear cumulative obligation curve for an award over given dates.
 * Distributes total_obligation linearly over the period of performance.
 * Used for IDV group visualization when per-award time series isn't fetched.
 */
export function estimateLinearCurve(
  award: AwardPortfolioItem,
  dates: Date[],
): CumulativePoint[] {
  const startStr = award.period_of_performance_start_date
  const endStr = award.period_of_performance_current_end_date

  if (!startStr || !endStr) {
    return dates.map((d) => ({ x: d, y: 0 }))
  }

  const startTime = new Date(startStr).getTime()
  const endTime = new Date(endStr).getTime()
  const duration = endTime - startTime

  const obl = safeNum(award.total_obligation)

  if (duration <= 0) {
    return dates.map((d) => ({ x: d, y: obl }))
  }

  return dates.map((d) => {
    const t = d.getTime()
    if (t <= startTime) return { x: d, y: 0 }
    if (t >= endTime) return { x: d, y: obl }
    return { x: d, y: obl * ((t - startTime) / duration) }
  })
}
